import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type {
  Group,
  Option,
} from '@mylighthouse/prism-ember/types/components/prism/combo-select-input';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import { t } from 'ember-intl';
import type { TriggerCentralMappingVariant } from 'lumen/components/settings/central-mapping/dropdowns/triggers/trigger-central-mapping';
import SettingsCentralMappingDropdownsTriggersTriggerCentralMapping from 'lumen/components/settings/central-mapping/dropdowns/triggers/trigger-central-mapping';
import type { CentralMappingCategory } from 'lumen/models';
import {
  AUTOMAPPING_STATUS,
  type AutomappingStatus,
} from 'lumen/utils/central-mapping-constants';

type DropdownMappingOption = { id?: string } & (Group<Option> | Option);

interface SettingsCentralMappingDropdownsDropdownMappingSignature {
  Args: {
    autoCategory: CentralMappingCategory | null;
    category: CentralMappingCategory | null;
    hideAutomappingVisuals?: boolean;
    hierarchyCategories: CentralMappingCategory[];
    isMixed?: boolean;
    isReadOnly?: boolean;
    isSelected?: boolean;
    onOverrideAutomapping?: () => void;
    setCategory: (category: CentralMappingCategory | null) => void;
    toggleAutomapping?: (category: CentralMappingCategory | null) => void;
    userCategory: CentralMappingCategory | null;
    variant: TriggerCentralMappingVariant;
  };
}

export default class SettingsCentralMappingDropdownsDropdownMapping extends Component<SettingsCentralMappingDropdownsDropdownMappingSignature> {
  @service declare intl: Services['intl'];

  get actionToggleAutomapping():
    | {
        buttonType?: 'intelligence-secondary';
        dataTestId?: string;
        iconBefore?: IconName;
        label: string;
        onClick: () => void;
      }
    | undefined {
    if (this.canEnableAutomation) {
      return {
        label: this.intl.t('settings.central_mapping.enable_automation'),
        // eslint-disable-next-line @typescript-eslint/unbound-method
        onClick: this.toggleAutomapping,
        buttonType: 'intelligence-secondary',
        iconBefore: 'automation',
        dataTestId: 'dropdown-mapping-enable-automapping',
      };
    }

    if (this.mappingStatus === AUTOMAPPING_STATUS.AUTO_MAPPED) {
      return {
        label: this.intl.t('settings.central_mapping.disable_automation'),
        // eslint-disable-next-line @typescript-eslint/unbound-method
        onClick: this.toggleAutomapping,
        iconBefore: 'checkmark',
        dataTestId: 'dropdown-mapping-disable-automapping',
      };
    }

    return undefined;
  }

  get breadcrumbs(): string[] {
    const { category } = this.args;

    if (!category) {
      return [];
    }

    return category.path.map((cat) => cat.name);
  }

  get canEnableAutomation(): boolean {
    const { autoCategory, userCategory, hideAutomappingVisuals } = this.args;

    // An auto-mapped value the user has overridden can be handed back to automation,
    // whether or not the override matches the auto-mapped value.
    return Boolean(!hideAutomappingVisuals && autoCategory && userCategory);
  }

  @cached
  get mappingOptions(): DropdownMappingOption[] {
    const { autoCategory } = this.args;
    const mappingEntryHasAutoMappedCategory = !isEmpty(autoCategory);

    const rootCategories = this.args.hierarchyCategories.filter(
      (category) => category.depth === 0,
    );

    const options = this.buildOptions(rootCategories);

    // Auto-mapped categories can't be unmapped.
    if (!mappingEntryHasAutoMappedCategory) {
      const unmappedOption: DropdownMappingOption = {
        id: this.unmappedOption.id,
        label: this.intl.t('general.unmapped'),
      };

      options.push(unmappedOption);
    }

    return options;
  }

  get mappingStatus(): AutomappingStatus {
    const { userCategory, autoCategory, category, hideAutomappingVisuals } =
      this.args;

    if (
      !hideAutomappingVisuals &&
      userCategory &&
      autoCategory &&
      userCategory === autoCategory
    ) {
      return AUTOMAPPING_STATUS.AUTO_MAPPED_FROZEN;
    }

    if (userCategory) {
      return AUTOMAPPING_STATUS.USER_MAPPED;
    }

    if (!hideAutomappingVisuals && autoCategory) {
      return AUTOMAPPING_STATUS.AUTO_MAPPED;
    }

    // Edge case: when bulk selecting a mix of frozen auto-mapped, auto-mapped and user-mapped categories,
    // the bulk dropdown should show the user-mapped status.
    // See `bulkUserCategory`, `bulkAutoCategory` and `bulkCategory` getters in the `BulkEditRow` component.
    if (category && !userCategory && !autoCategory) {
      return AUTOMAPPING_STATUS.USER_MAPPED;
    }

    // This prevents the mapping status from being seen as `unmapped`.
    if (category && hideAutomappingVisuals) {
      return AUTOMAPPING_STATUS.USER_MAPPED;
    }

    return AUTOMAPPING_STATUS.UNMAPPED;
  }

  get unmappedOption(): DropdownMappingOption {
    return {
      id: 'unmapped',
      label: this.intl.t('general.unmapped'),
    };
  }

  buildOptions(categories: CentralMappingCategory[]): DropdownMappingOption[] {
    // @ts-expect-error: Type '({ id: string | null; label: string; icon: string | undefined; unsafe_className: string | undefined; groupName?: undefined; options?: undefined; unsafe_isCollapsed?: undefined; unsafe_isSelectable?: undefined; } | { ...; })[]' is not assignable to type 'DropdownMappingOption[]'.
    return categories.map((category) => {
      const { autoCategory, userCategory, hideAutomappingVisuals } = this.args;

      const children = category.children?.slice();

      let icon;
      if (!hideAutomappingVisuals && category === autoCategory) {
        if (userCategory === autoCategory) {
          // Option is the frozen automapped option
          icon = 'automation-off';
        } else {
          // Option is the automapped option
          icon = 'automation';
        }
      }

      const isAutoMappedCategory =
        // Option should be coloured purple if it's the automapped option.
        category === autoCategory &&
        // Check that it's not the frozen automapped option, because then it should be the default colour.
        userCategory !== autoCategory &&
        // Check that the automapping visuals are not hidden, because then it should be the default colour.
        !hideAutomappingVisuals;
      if (!children.length) {
        return {
          id: category.id,
          label: category.name,
          icon,
          unsafe_className: isAutoMappedCategory
            ? 'mapping-dropdown__option--automation'
            : undefined,
        };
      }

      return {
        id: category.id,
        label: category.name,
        groupName: category.name,
        options: this.buildOptions(children).map((subCategory) => ({
          ...subCategory,
          groupId: category.id,
        })),
        icon,
        unsafe_className: isAutoMappedCategory
          ? 'mapping-dropdown__option--automation'
          : undefined,
        unsafe_isCollapsed: true,
        unsafe_isSelectable: true,
      };
    });
  }

  @action onChange(
    option: Readonly<Option | Option[] | null | undefined>,
  ): void {
    const {
      hierarchyCategories,
      autoCategory,
      hideAutomappingVisuals,
      setCategory,
      onOverrideAutomapping,
    } = this.args;

    const typedOption = option as DropdownMappingOption;

    const categoryId = typedOption.id;
    const foundCategory =
      hierarchyCategories.find((category) => category.id === categoryId) ??
      null;

    if (
      this.mappingStatus === AUTOMAPPING_STATUS.AUTO_MAPPED &&
      onOverrideAutomapping &&
      foundCategory !== autoCategory
    ) {
      onOverrideAutomapping();
    }

    if (foundCategory && foundCategory === autoCategory) {
      // Prohibit users from selecting the category that is already selected, when it has the `automapped_frozen` status.
      // Because it's weird that it would then change to the `automapped` status.
      if (this.mappingStatus === AUTOMAPPING_STATUS.AUTO_MAPPED_FROZEN) {
        return;
      }

      if (hideAutomappingVisuals) {
        // Allow users to select the category that is already selected as automapped,
        // only when it has the hide automapping visuals.
        setCategory(autoCategory);
      } else {
        // This prevents an automapped value converting to usermapped because a user re-selects it.
        setCategory(null);
      }
    } else {
      setCategory(foundCategory);
    }
  }

  @action toggleAutomapping(): void {
    const { toggleAutomapping, autoCategory } = this.args;
    if (!toggleAutomapping) {
      return;
    }

    toggleAutomapping(this.canEnableAutomation ? null : autoCategory);
  }

  <template>
    <PrismComboSelectInput
      @disabled={{@isReadOnly}}
      @onChange={{this.onChange}}
      @options={{this.mappingOptions}}
      @unsafe_additionalAction={{unless
        @hideAutomappingVisuals
        this.actionToggleAutomapping
      }}
      @unsafe_collapsibleGroupBackLabel={{t
        "settings.pms_mapping.back_to_categories"
      }}
      @unsafe_dropdownClass="mapping-dropdown"
      @unsafe_triggerComponent={{component
        SettingsCentralMappingDropdownsTriggersTriggerCentralMapping
        breadcrumbs=this.breadcrumbs
        isMixed=@isMixed
        isReadOnly=@isReadOnly
        isSelected=@isSelected
        mappingStatus=this.mappingStatus
        variant=@variant
      }}
      @value={{null}}
      data-test-id="mapping-dropdown"
    />
  </template>
}
